import { useCallback, useEffect, useState } from 'react';

/**
 * Extracts a SportSweeps-shaped access code from arbitrary clipboard text.
 * Access codes are 4–10 alphanumeric chars. Strips common chatter like
 * "Join: ABC123!" or "Use code XYZ789 to enter." and returns the most
 * likely candidate. Prefers, in order:
 *   1. A code that follows the word "code" / "codes".
 *   2. A code in the trailing path segment of a URL.
 *   3. The first ALL-CAPS / mixed-case alphanumeric chunk that contains a
 *      digit OR is all uppercase (skipping ordinary English words).
 */
export function extractCodeFromClipboard(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();

  // 1) Prefer a code that follows the word "code" / "codes" / "join code".
  const labelled = trimmed.match(/\bcodes?\b[^A-Za-z0-9]{0,5}([A-Za-z0-9]{4,10})\b/i);
  if (labelled) return labelled[1].toUpperCase();

  // 2) URL path segment — sportsweeps.io/joinpool/XXXXXX
  const urlMatch = trimmed.match(/\/joinpool\/([A-Za-z0-9]{4,10})\b/i);
  if (urlMatch) return urlMatch[1].toUpperCase();

  // 3) Fallback — first 4–10 char alphanumeric chunk that *looks* like an
  //    access code: either contains a digit or is all uppercase. This
  //    avoids picking up generic English words like "Multi" or "Hello".
  const candidates = trimmed.match(/\b[A-Za-z0-9]{4,10}\b/g) || [];
  for (const c of candidates) {
    if (/\d/.test(c) || c === c.toUpperCase()) return c.toUpperCase();
  }
  return '';
}

/**
 * Returns a `paste()` helper that reads navigator.clipboard, extracts an
 * access-code-shaped string, and invokes `onCode(code)` when one is found.
 * Also tracks a `supported` flag so callers can hide the UI affordance on
 * browsers without the Async Clipboard API (e.g. older iOS, Firefox in
 * non-HTTPS contexts).
 */
export function useClipboardCode(onCode) {
  const [supported, setSupported] = useState(false);
  const [pasting, setPasting] = useState(false);

  useEffect(() => {
    setSupported(
      typeof navigator !== 'undefined' &&
      !!navigator.clipboard &&
      typeof navigator.clipboard.readText === 'function'
    );
  }, []);

  const paste = useCallback(async () => {
    if (!navigator?.clipboard?.readText) return null;
    setPasting(true);
    try {
      const text = await navigator.clipboard.readText();
      const code = extractCodeFromClipboard(text);
      if (code && onCode) onCode(code);
      return code || null;
    } catch {
      // Permission denied or unsupported — no-op (button stays available
      // so the user can retry; iOS prompts the first time, then remembers).
      return null;
    } finally {
      setPasting(false);
    }
  }, [onCode]);

  return { paste, supported, pasting };
}
